import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Checkbox, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { listUsers } from '../../services/SecurityUsersService';

// Usuarios de la cuenta (GET /useraccount/users): correo y nombre vienen de
// Cognito. Un id guardado que ya no está en la cuenta se muestra tal cual.
export const userOptionLabel = (user) => user?.email || user?.name || user?.user_id || '';

// Sin mayúsculas ni acentos: "perez" encuentra a "Pérez".
const normalizeSearch = (value) =>
  String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const filterUserOptions = (options, { inputValue }) => {
  const term = normalizeSearch(inputValue).trim();
  if (!term) {
    return options;
  }
  return options.filter((user) =>
    [user.email, user.name, user.user_id].some((value) => value && normalizeSearch(value).includes(term))
  );
};

/**
 * Usuarios de la cuenta indexados por user_id. Carga al pasar `enabled` a
 * true (p. ej. al abrir un diálogo) y no vuelve a pedir mientras siga true.
 */
export const useAccountUsers = (enabled) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    listUsers()
      .then((response) => {
        if (!cancelled) {
          setUsers(Array.isArray(response?.items) ? response.items.filter((user) => user?.user_id) : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('No se pudo cargar la lista de usuarios.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const usersById = useMemo(() => new Map(users.map((user) => [user.user_id, user])), [users]);
  return { users, usersById, loading, error };
};

/**
 * Selector múltiple de usuarios de la cuenta con búsqueda por correo y
 * nombre. Trabaja con ids (`value`/`onChange`), que es lo que se guarda.
 */
const AccountUsersAutocomplete = ({ value = [], onChange, enabled = true, chipSx }) => {
  const { users, loading, error } = useAccountUsers(enabled);

  const options = useMemo(() => {
    const known = [...users].sort((a, b) => userOptionLabel(a).localeCompare(userOptionLabel(b)));
    const knownIds = new Set(known.map((user) => user.user_id));
    const missing = value
      .filter((userId) => !knownIds.has(userId))
      .map((userId) => ({ user_id: userId, notInAccount: true }));
    return [...known, ...missing];
  }, [users, value]);

  const selected = useMemo(() => {
    const byId = new Map(options.map((user) => [user.user_id, user]));
    return value.map((userId) => byId.get(userId) || { user_id: userId, notInAccount: true });
  }, [options, value]);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={options}
      value={selected}
      onChange={(_event, picked) => onChange?.(picked.map((user) => user.user_id))}
      getOptionLabel={userOptionLabel}
      filterOptions={filterUserOptions}
      isOptionEqualToValue={(option, current) => option.user_id === current.user_id}
      loading={loading}
      loadingText="Cargando usuarios..."
      noOptionsText="No se encontraron usuarios."
      renderOption={(props, option, { selected: isSelected }) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <Checkbox
              checked={isSelected}
              size="small"
              sx={{ mr: 1, color: 'var(--wb-text-label)', '&.Mui-checked': { color: 'var(--wb-accent)' } }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ color: 'var(--wb-text)' }}>
                {option.email || option.user_id}
              </Typography>
              <Typography variant="caption" noWrap component="div" sx={{ color: 'var(--wb-text-label)' }}>
                {option.notInAccount ? 'Usuario fuera de la cuenta' : option.name || 'Sin nombre'}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderTags={(tagged, getTagProps) =>
        tagged.map((user, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              size="small"
              label={userOptionLabel(user)}
              title={user.name ? `${user.name} · ${user.email || user.user_id}` : undefined}
              sx={chipSx || { bgcolor: 'var(--wb-border-20)', color: 'var(--wb-text)' }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length ? '' : 'Busca por correo o nombre'}
          size="small"
          error={Boolean(error)}
          helperText={error || undefined}
          InputProps={{
            ...params.InputProps,
            sx: { color: 'var(--wb-text)' },
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      slotProps={{
        paper: { sx: { bgcolor: 'var(--wb-bg-panel)', color: 'var(--wb-text)' } }
      }}
    />
  );
};

export default AccountUsersAutocomplete;
